import { useCallback, useMemo, useRef, useState } from "react";
import { EditorAttachmentType, EditorSubmitPayloadType } from "@ir/ui";
import { useUser } from "@/hooks";
import { Conversation } from "@/core/entities/conversation/Conversation";
import {
  GetConversationByReferenceIdUseCase,
  GetMessagesUseCase,
  SendMessageUseCase,
  UploadConversationAttachmentUseCase,
} from "@/core/usecases/conversation";
import { ConversationRepository } from "@/infrastructure/repositories/conversation/Conversation.repository";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl/GraphQl.adapter";
import { ConversationUtils, GroupedMessages } from "../utils/ConversationUtils";
import { IUploadAttachment, IUploadAttachmentResponse } from "@/core/repositories/conversation";
import { NewConversationMessagePayload } from "@/core/events/notificationEventsMap";
import { ECurrentMessageReceiveAction, InternalConversationContextType, SendMessageOptions } from "../interface/InternalConversationContextType";
import { ConversationMessage, IAttachment, IMessage } from "@/core/entities/conversation";
import { CategoryKey } from "@/core/entities/deduction";

const INITIAL_PAGE_LIMIT = 20;
const conversationRepository = new ConversationRepository(GraphQlAdapter.getInstance().getClient());
const getConversationByReferenceId = new GetConversationByReferenceIdUseCase(conversationRepository);
const getConversationMessages = new GetMessagesUseCase(conversationRepository);
const sendMessageUseCase = new SendMessageUseCase(conversationRepository);
const uploadConversationAttachmentUseCase = new UploadConversationAttachmentUseCase(conversationRepository);

export const useInternalConversationService = (): InternalConversationContextType => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [groupedMessages, setGroupedMessages] = useState<GroupedMessages[]>([]);
  const [lastReceivedMessage, setLastReceivedMessage] = useState<NewConversationMessagePayload | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentMessageReceiveAction, setCurrentMessageReceiveAction] = useState<ECurrentMessageReceiveAction | null>(
    ECurrentMessageReceiveAction.INITIAL_LOAD
  );

  const currentPage = useRef<number>(1);
  const [isError, setIsError] = useState(false);
  const { user } = useUser();

  /**
   ********************* Reset Conversation State *********************
   */
  const resetConversationState = useCallback(() => {
    setIsInitialLoading(true);
    setIsError(false);
    setGroupedMessages([]);
    setHasMore(false);
    currentPage.current = 1;
    setCurrentMessageReceiveAction(ECurrentMessageReceiveAction.INITIAL_LOAD);
  }, [setIsInitialLoading, setIsError, setGroupedMessages, setHasMore, currentPage, setCurrentMessageReceiveAction]);

  /**
   ********************* Set Messages to State *********************
   */
  const setMessagesToState = useCallback(
    (messages: ConversationMessage[]) => {
      setGroupedMessages(prev => ConversationUtils.groupMessagesByDate(prev, messages));
    },
    [setGroupedMessages]
  );

  /**
   ********************* Fetch Conversation by Reference Id and Category Key *********************
   */
  const fetchConversation = useCallback(
    async (referenceId: number, categoryKey: CategoryKey): Promise<Conversation | null> => {
      if (!referenceId) return null;
      resetConversationState();
      try {
        const conversation = await getConversationByReferenceId.execute({
          referenceId,
          categoryKey,
        });
        setConversation(conversation);
        setIsError(false);

        return conversation;
      } catch {
        setIsInitialLoading(false);
        setIsError(true);
      }
      return null;
    },
    [setConversation, resetConversationState]
  );

  /**
   ********************* Fetch Messages by Conversation ID *********************
   */
  const fetchMessages = useCallback(async (): Promise<ConversationMessage[]> => {
    const _currentPage = currentPage.current ?? 1;
    const _pageLimit = INITIAL_PAGE_LIMIT ?? 20;
    if (!conversation) return [];
    setIsError(false);

    if (_currentPage === 1) {
      setCurrentMessageReceiveAction(ECurrentMessageReceiveAction.INITIAL_LOAD);
      setGroupedMessages([]);
    } else {
      setIsLoadingMore(true);
      setCurrentMessageReceiveAction(ECurrentMessageReceiveAction.LOAD_MORE);
    }

    try {
      const response = await getConversationMessages.execute(
        { conversationId: conversation.id, page: _currentPage, limit: _pageLimit },
        true
      );

      if (_currentPage === 1) {
        setIsInitialLoading(false);
      } else {
        setIsLoadingMore(false);
      }

      setHasMore(response.hasMore);
      if (response.hasMore) {
        currentPage.current = _currentPage + 1;
      }
      setMessagesToState(response.messages);

      return response.messages;
    } catch {
      setIsLoadingMore(false);
      setIsInitialLoading(false);
      setIsError(true);
    } finally {
      setIsLoadingMore(false);
      setIsInitialLoading(false);
    }
    return [];
  }, [conversation, currentPage, setMessagesToState, setIsInitialLoading, setIsLoadingMore]);

  /**
   ******** Before Sending Message to the Conversation - Remove Empty Tags ***********
   */

  const removeEmptyTags = (html: string) => {
    if (!html) return html;

    // Remove <p><br></p> and empty <p></p>
    // Using bounded quantifiers to prevent catastrophic backtracking
    // Limiting whitespace to prevent exponential backtracking
    const emptyParagraphWithBr = /<p>\s{0,20}<br\s{0,5}\/?>\s{0,20}<\/p>/gi;
    const emptyParagraph = /<p>\s{0,20}<\/p>/gi;
    let cleaned = html.replaceAll(emptyParagraphWithBr, "").replaceAll(emptyParagraph, "");

    // Remove empty Quill list items:
    // <li data-list="..."><span class="ql-ui"...></span><br></li>
    // Using bounded quantifiers and more specific patterns to prevent backtracking
    // Limiting attribute length to 200 chars and whitespace to 20 chars
    const emptyListItem =
      /<li[^>]{0,200}>\s{0,20}(<span class="ql-ui"[^>]{0,200}><\/span>)?\s{0,20}<br\s{0,5}\/?>\s{0,20}<\/li>/gi;
    cleaned = cleaned.replaceAll(emptyListItem, "");

    return cleaned.trim();
  };

  /**
   ********************* Send Message to the Conversation *********************
   */
  const sendMessage = useCallback(
    async (
      data: EditorSubmitPayloadType,
      invoiceNumber: string,
      options?: SendMessageOptions
    ): Promise<{ action: ECurrentMessageReceiveAction; message: ConversationMessage | null }> => {
      if (!conversation?.id || !user) return { action: ECurrentMessageReceiveAction.INITIAL_LOAD, message: null };

      const cleanedHtmlContent = removeEmptyTags(data.contentHtml);

      const requestUniqueKey = `${conversation?.id}-${Date.now()}`;
      const messageHtmlContent = cleanedHtmlContent;
      const messageJsonContent = data.contentDelta;
      const attachments = data?.attachments?.map((attachment: EditorAttachmentType) => {
        const attachmentResponse = attachment.uploadResponse.attachment as IUploadAttachment;
        return {
          fileName: attachmentResponse.fileName,
          fileDisplayName: attachmentResponse.displayName,
          filePath: attachmentResponse.fileName,
          fileSize: attachmentResponse.size,
          fileMimeType: attachmentResponse.mimeType,
        };
      });

      const createdAt = new Date().toISOString();
      const temporaryMessage: IMessage = {
        id: null,
        conversationId: conversation.id,
        requestUniqueKey: requestUniqueKey,
        createdAt: createdAt,
        contentHtml: messageHtmlContent,
        isOwner: true,
        isError: false,
        updatedAt: null,
        attachments: attachments as IAttachment[],
        sender: {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          fullName: user.fullName,
          isDeleted: false,
          avatar: user.avatar ?? null,
        },
      };

      const newMessageBeforePost = new ConversationMessage(temporaryMessage);

      // Optimistic UI: notify parent immediately so message appears before API completes
      options?.onOptimisticMessage?.(newMessageBeforePost);

      setMessagesToState([newMessageBeforePost]);
      setCurrentMessageReceiveAction(ECurrentMessageReceiveAction.SEND_MESSAGE);

      try {
        await sendMessageUseCase.execute({
          conversationId: conversation.id,
          requestUniqueKey: requestUniqueKey,
          contentHtml: messageHtmlContent,
          contentJson: messageJsonContent,
          invoiceNumber: invoiceNumber,
          referenceId: conversation.referenceId,
          categoryKey: conversation.categoryKey,
          attachments,
        });
        return { action: ECurrentMessageReceiveAction.SEND_MESSAGE, message: newMessageBeforePost };
      } catch {
        const updatedGroupedMessages = ConversationUtils.updateMessageFailedStatus(groupedMessages, requestUniqueKey);
        setMessagesToState(ConversationUtils.getFlattenMessages(updatedGroupedMessages));
        const failedMessage = ConversationUtils.getFlattenMessages(updatedGroupedMessages).find(
          message => message.requestUniqueKey === requestUniqueKey
        );
        return { action: ECurrentMessageReceiveAction.SEND_MESSAGE, message: failedMessage ?? newMessageBeforePost };
      }
    },
    [conversation, user, setMessagesToState, groupedMessages]
  );

  /**
   ********************* Receive New Conversation Message *********************
   */
  const receiveNewConversationMessage = useCallback(
    (payload: NewConversationMessagePayload) => {
      if (conversation?.id !== payload.conversationId) {
        return;
      }

      setCurrentMessageReceiveAction(ECurrentMessageReceiveAction.RECEIVE_NEW_MESSAGE);
      const newMessage = new ConversationMessage({
        id: payload.id,
        attachments: payload.attachments,
        contentHtml: payload.contentHtml,
        conversationId: payload.conversationId,
        createdAt: payload.createdAt,
        isError: false,
        isOwner: payload.isOwner,
        requestUniqueKey: payload.requestUniqueKey,
        sender: payload.sender,
        updatedAt: null,
      });
      setLastReceivedMessage(payload);
      setMessagesToState([newMessage]);
    },
    [conversation, setMessagesToState]
  );

  /**
   ********************* Upload Conversation Attachment *********************
   */
  const uploadConversationAttachment = useCallback(async (file: File): Promise<IUploadAttachmentResponse> => {
    const newFile = new File([file], ConversationUtils.sanitizeFileName(file.name), {
      type: ConversationUtils.getFileType(file),
    });

    const response = await uploadConversationAttachmentUseCase.execute({ file: newFile });
    return response;
  }, []);

  return useMemo(
    () => ({
      conversation,
      groupedMessages,
      currentMessageReceiveAction,
      setCurrentMessageReceiveAction,
      lastReceivedMessage,
      isInitialLoading,
      isLoadingMore,
      currentPage: currentPage.current ?? 1,
      hasMore,
      isError,
      fetchConversation,
      fetchMessages,
      sendMessage,
      receiveNewConversationMessage,
      uploadConversationAttachment,
      resetConversationState,
    }),
    [
      conversation,
      groupedMessages,
      currentMessageReceiveAction,
      setCurrentMessageReceiveAction,
      lastReceivedMessage,
      isInitialLoading,
      isLoadingMore,
      currentPage,
      hasMore,
      isError,
      fetchConversation,
      fetchMessages,
      sendMessage,
      receiveNewConversationMessage,
      uploadConversationAttachment,
      resetConversationState,
    ]
  );
};
